// SPDX-License-Identifier: Apache-2.0

// usePresence — Solid hook over Yjs awareness, layered on top of the
// `YjsSignalRoom` Durable Object signaling worker.
//
// Wire shape (matches `apps/mcp/workers/yjs-room.ts`):
//
//   client → server  { type: "subscribe",   topics: [room] }
//   client → server  { type: "publish", topic: room, ...payload }
//   server → client   echoes published payload to all topic peers
//
// The DO does not parse Yjs payloads — it just relays the envelope. So we
// pack our own awareness sync into `payload`:
//
//   { kind: "awareness-update", clientId, update: <base64 bytes> }
//   { kind: "awareness-query"  }
//
// `awareness-query` asks already-joined peers to re-broadcast their state so
// late joiners see them within one round-trip. Without it, a peer that was
// already in the room when we arrive is invisible until they next move.
//
// Falls back gracefully when no signaling URL is reachable: the hook still
// surfaces a `self()` accessor and an empty `peers()` so the UI works in
// solo mode and we don't tear down on every accidental dev-server reload.
//
// LIFECYCLE
//   - One Y.Doc + Awareness per roomId. Keyed cache so navigating between
//     vault routes with the same room reuses the connection.
//   - `setSelf()` writes via awareness; remote awareness-change events fan
//     into `peers()`.
//   - `onCleanup` removes our awareness state, sends an unsubscribe, and
//     closes the WS.

import {
  createSignal,
  createEffect,
  onCleanup,
  type Accessor,
} from "solid-js";
import * as Y from "yjs";
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from "./awareness";

export interface PeerPresence {
  clientId: number;
  name: string;
  color: string;
  cursor?: { docId: string; ch: number };
  lastSeen: number;
}

export interface UsePresenceReturn {
  peers: Accessor<PeerPresence[]>;
  self: Accessor<PeerPresence | null>;
  setSelf: (patch: Partial<Omit<PeerPresence, "clientId" | "lastSeen">>) => void;
}

// ─── module-level keyed cache ───────────────────────────────────────────────
//
// Shared across all hook instances so two components in the same render tree
// don't open two WebSockets to the same room. Refcount on enter/leave; when
// the count hits zero we tear down.

interface RoomState {
  yDoc: Y.Doc;
  awareness: Awareness;
  ws: WebSocket | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  refcount: number;
  /** ms epoch at which a remote awareness-update last lit up each peer; used
   *  by stale-peer pruning. */
  lastSeenByClient: Map<number, number>;
}

const rooms = new Map<string, RoomState>();

// ─── signaling URL resolution ────────────────────────────────────────────────
//
// Order:
//   1. `import.meta.env.VITE_YJS_URL` — explicit override (e.g. for staging).
//   2. Production hostname (tether.dev) → wss://<host>/yjs/<room>.
//   3. Otherwise → null (in-browser-only single-tab mode). The local Hono on
//      :5417 does NOT mount /yjs — only the deployed CF Worker does. So in
//      dev, two browser tabs talking to localhost:5173 will not see each
//      other unless the user sets VITE_YJS_URL to the deployed worker.

function resolveSignalingUrl(roomId: string): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const envUrl: string | undefined = (import.meta as any)?.env?.VITE_YJS_URL;
  if (envUrl && envUrl.length > 0) {
    const base = envUrl.replace(/\/$/, "");
    return `${base}/${encodeURIComponent(roomId)}`;
  }
  if (typeof window === "undefined") return null;
  const host = window.location.host;
  // Match prod-domain hosts; locally we have no /yjs mount on :5417.
  if (
    host === "tether.dev" ||
    host === "www.tether.dev"
  ) {
    return `wss://${host}/yjs/${encodeURIComponent(roomId)}`;
  }
  // TODO(multi-tab dev): expose VITE_YJS_URL in `.env.development.local` once
  // someone wants to drive the Worker for cross-tab presence on localhost.
  return null;
}

// ─── envelope helpers ────────────────────────────────────────────────────────

function bytesToBase64(buf: Uint8Array): string {
  let s = "";
  for (let i = 0; i < buf.length; i++) s += String.fromCharCode(buf[i]!);
  return typeof btoa === "function" ? btoa(s) : Buffer.from(s, "binary").toString("base64");
}
function base64ToBytes(b64: string): Uint8Array {
  const bin = typeof atob === "function" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ─── room acquisition ───────────────────────────────────────────────────────

function acquireRoom(roomId: string): RoomState {
  const existing = rooms.get(roomId);
  if (existing) {
    existing.refcount += 1;
    return existing;
  }
  const yDoc = new Y.Doc();
  const awareness = new Awareness(yDoc);
  const state: RoomState = {
    yDoc,
    awareness,
    ws: null,
    reconnectTimer: null,
    refcount: 1,
    lastSeenByClient: new Map(),
  };
  rooms.set(roomId, state);

  const url = resolveSignalingUrl(roomId);
  if (url) connect(roomId, state, url);
  return state;
}

function releaseRoom(roomId: string): void {
  const room = rooms.get(roomId);
  if (!room) return;
  room.refcount -= 1;
  if (room.refcount > 0) return;
  // Last user gone — tear down.
  if (room.reconnectTimer) {
    clearTimeout(room.reconnectTimer);
    room.reconnectTimer = null;
  }
  if (room.ws && room.ws.readyState <= WebSocket.OPEN) {
    try {
      room.ws.send(JSON.stringify({ type: "unsubscribe", topics: [roomId] }));
    } catch {
      /* swallow */
    }
    try {
      room.ws.close(1000, "released");
    } catch {
      /* swallow */
    }
  }
  // Clear our local awareness so dangling state isn't accidentally re-sent.
  try {
    removeAwarenessStates(room.awareness, [room.yDoc.clientID], "released");
  } catch {
    /* swallow */
  }
  rooms.delete(roomId);
}

// ─── WS connect + reconnect ──────────────────────────────────────────────────

const RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

function connect(roomId: string, room: RoomState, url: string, attempt = 0): void {
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[presence] WS construct failed:", err);
    scheduleReconnect(roomId, room, url, attempt + 1);
    return;
  }
  room.ws = ws;

  ws.addEventListener("open", () => {
    try {
      ws.send(JSON.stringify({ type: "subscribe", topics: [roomId] }));
    } catch {
      /* swallow */
    }
    // Push our current awareness state so already-connected peers see us.
    broadcastAwareness(roomId, room, [room.yDoc.clientID]);
    // Ask everyone else to re-broadcast theirs.
    try {
      ws.send(
        JSON.stringify({ type: "publish", topic: roomId, kind: "awareness-query" }),
      );
    } catch {
      /* swallow */
    }
  });

  ws.addEventListener("message", (ev: MessageEvent) => {
    let msg: { type?: string; kind?: string; clientId?: number; update?: string } | null = null;
    try {
      const raw = typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data as ArrayBuffer);
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    if (!msg || typeof msg !== "object") return;
    // The DO relays our publishes back to us too — ignore our own.
    if (msg.kind === "awareness-update" && typeof msg.update === "string") {
      if (typeof msg.clientId === "number" && msg.clientId === room.yDoc.clientID) return;
      try {
        const bytes = base64ToBytes(msg.update);
        applyAwarenessUpdate(room.awareness, bytes, "remote");
        if (typeof msg.clientId === "number") {
          room.lastSeenByClient.set(msg.clientId, Date.now());
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[presence] applyAwarenessUpdate failed:", err);
      }
      return;
    }
    if (msg.kind === "awareness-query") {
      // Someone joined and wants our state — fire ours back.
      broadcastAwareness(roomId, room, [room.yDoc.clientID]);
      return;
    }
    // ping/pong/etc. flow through the DO; nothing to do here.
  });

  ws.addEventListener("close", () => {
    if (room.ws === ws) room.ws = null;
    if (room.refcount > 0) scheduleReconnect(roomId, room, url, attempt + 1);
  });
  ws.addEventListener("error", () => {
    // 'error' is followed by 'close' — let the close handler reconnect.
  });
}

function scheduleReconnect(roomId: string, room: RoomState, url: string, attempt: number): void {
  if (room.reconnectTimer) return;
  const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 15_000;
  room.reconnectTimer = setTimeout(() => {
    room.reconnectTimer = null;
    if (room.refcount > 0) connect(roomId, room, url, attempt);
  }, delay);
}

// Pack the awareness state for the listed clients into an envelope and send.
// We piggyback on the y-protocols awareness encoding so we get correct
// add/remove/timeout semantics inside the Awareness instance, but the
// message is wrapped in our own JSON envelope so it crosses the relay.
function broadcastAwareness(roomId: string, room: RoomState, clients: number[]): void {
  if (!room.ws || room.ws.readyState !== WebSocket.OPEN) return;
  let encoded: Uint8Array;
  try {
    encoded = encodeAwarenessUpdate(room.awareness, clients);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[presence] encodeAwarenessUpdate failed:", err);
    return;
  }
  try {
    room.ws.send(
      JSON.stringify({
        type: "publish",
        topic: roomId,
        kind: "awareness-update",
        clientId: room.yDoc.clientID,
        update: bytesToBase64(encoded),
      }),
    );
  } catch {
    /* swallow */
  }
}

// ─── the hook ────────────────────────────────────────────────────────────────

export function usePresence(roomId: () => string | undefined): UsePresenceReturn {
  const [peers, setPeers] = createSignal<PeerPresence[]>([]);
  const [self, setSelfSig] = createSignal<PeerPresence | null>(null);
  // The currently-pending self patch — held here so callers can `setSelf`
  // before the room is bound, and the value is flushed on bind.
  let pendingSelf: Partial<Omit<PeerPresence, "clientId" | "lastSeen">> = {};
  let currentRoomId: string | undefined;
  let currentRoom: RoomState | null = null;
  let awarenessSub: (() => void) | null = null;

  function readRemotePeers(room: RoomState): PeerPresence[] {
    const states = room.awareness.getStates();
    const out: PeerPresence[] = [];
    const now = Date.now();
    for (const [clientId, raw] of states.entries()) {
      if (clientId === room.yDoc.clientID) continue;
      const r = (raw ?? {}) as Partial<PeerPresence>;
      out.push({
        clientId,
        name: typeof r.name === "string" ? r.name : "anon",
        color: typeof r.color === "string" ? r.color : "#888",
        cursor: r.cursor,
        lastSeen: room.lastSeenByClient.get(clientId) ?? now,
      });
    }
    // Stable ordering by clientId — keeps avatar stack from flickering.
    out.sort((a, b) => a.clientId - b.clientId);
    return out;
  }

  function readSelf(room: RoomState): PeerPresence | null {
    const local = room.awareness.getLocalState() as Partial<PeerPresence> | null;
    if (!local) return null;
    return {
      clientId: room.yDoc.clientID,
      name: typeof local.name === "string" ? local.name : "you",
      color: typeof local.color === "string" ? local.color : "#888",
      cursor: local.cursor,
      lastSeen: Date.now(),
    };
  }

  createEffect(() => {
    const id = roomId();
    if (id === currentRoomId) return;

    // Detach from the previous room (if any).
    if (currentRoom && awarenessSub) {
      awarenessSub();
      awarenessSub = null;
    }
    if (currentRoomId !== undefined) {
      releaseRoom(currentRoomId);
    }
    currentRoomId = id;
    currentRoom = null;

    if (!id) {
      setPeers([]);
      setSelfSig(null);
      return;
    }

    const room = acquireRoom(id);
    currentRoom = room;

    // Flush any pending self patch into awareness now that we have a room.
    if (Object.keys(pendingSelf).length > 0) {
      const merged = { ...(room.awareness.getLocalState() ?? {}), ...pendingSelf };
      room.awareness.setLocalState(merged);
      pendingSelf = {};
    }

    // Subscribe to changes; recompute on every update.
    const handler = (
      changes: { added: number[]; updated: number[]; removed: number[] },
      _origin: unknown,
    ) => {
      const now = Date.now();
      for (const c of changes.added) room.lastSeenByClient.set(c, now);
      for (const c of changes.updated) room.lastSeenByClient.set(c, now);
      for (const c of changes.removed) room.lastSeenByClient.delete(c);

      // If a LOCAL change just fired, broadcast it.
      if ([...changes.added, ...changes.updated, ...changes.removed].includes(room.yDoc.clientID)) {
        broadcastAwareness(id, room, [room.yDoc.clientID]);
      }
      setPeers(readRemotePeers(room));
      setSelfSig(readSelf(room));
    };
    room.awareness.on("change", handler);
    awarenessSub = () => room.awareness.off("change", handler);

    // Prime initial signals.
    setPeers(readRemotePeers(room));
    setSelfSig(readSelf(room));
  });

  onCleanup(() => {
    if (currentRoom && awarenessSub) awarenessSub();
    awarenessSub = null;
    if (currentRoomId !== undefined) releaseRoom(currentRoomId);
    currentRoomId = undefined;
    currentRoom = null;
  });

  function setSelf(patch: Partial<Omit<PeerPresence, "clientId" | "lastSeen">>) {
    if (currentRoom) {
      const merged = { ...(currentRoom.awareness.getLocalState() ?? {}), ...patch };
      currentRoom.awareness.setLocalState(merged);
    } else {
      pendingSelf = { ...pendingSelf, ...patch };
    }
  }

  return { peers, self, setSelf };
}

// ─── helpers exposed for callers ─────────────────────────────────────────────

export const PEER_COLORS = [
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#f59e0b", // amber
  "#ec4899", // pink
  "#10b981", // emerald
  "#ef4444", // red
  "#3b82f6", // blue
  "#a855f7", // purple
] as const;

export function pickRandomColor(): string {
  return PEER_COLORS[Math.floor(Math.random() * PEER_COLORS.length)]!;
}
